// Детский режим (К-15, FR-KID-01…05): разминка → новое (сценарий + 2–3 задания) → награда.
// Изоляция (ARCHITECTURE 6.5): нет навигации, ссылок, покупок и полей ввода; выход — только через замок.
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Board, piecesFromScene, poseAt, type BoardHandle } from "@brovka/board";
import type { CompiledScenario } from "@brovka/content-types";
import { StarIcon } from "@brovka/ui";
import { ParentGate } from "../components/ParentGate.tsx";
import { audio } from "../lib/audio.ts";
import { findScenario, phraseUrl, recommended, uiText, uiUrl } from "../lib/content/catalog.ts";
import type { LoadedContent } from "../lib/content/store.ts";
import { db, type Child, type StoredEvent } from "../lib/db.ts";
import { record } from "../lib/events.ts";
import { QuizView } from "../quiz/QuizView.tsx";
import { runStep } from "../quiz/engine.ts";
import { t } from "../i18n/ru.ts";
import { initSession, step as reduce, type Plan, type Task } from "./session.ts";

/** План сессии: разминка — последнее задание в зеркальном варианте; новое — рекомендованный сценарий. */
export function makePlan(content: LoadedContent, child: Child, events: StoredEvent[]): Plan | null {
  const rec = recommended(content, child.stage, events);
  if (!rec) return null;
  const s = rec.scenario;
  const tasks: Task[] = [];
  for (const q of s.scenario.quizzes ?? []) {
    tasks.push({ scenarioId: s.scenario.id, quizId: q.id, variant: "original" });
    if (s.scenario.mirrorAllowed) tasks.push({ scenarioId: s.scenario.id, quizId: q.id, variant: "mirror" });
  }
  const last = [...events].filter((e) => e.type === "quiz_answered").sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))[0];
  let warmup: Task | null = null;
  if (last && typeof last.payload.quiz_id === "string") {
    const [sid, qid] = last.payload.quiz_id.split("/");
    const ws = sid ? findScenario(content, sid) : undefined;
    if (ws && qid && ws.scenario.scenario.quizzes?.some((q) => q.id === qid)) {
      warmup = { scenarioId: sid!, quizId: qid, variant: ws.scenario.scenario.mirrorAllowed ? "mirror" : "original", warmup: true };
    }
  }
  return { warmup, scenarioId: s.scenario.id, tasks: tasks.slice(0, 3) };
}

/** Сценарий с озвучкой каждого шага; дальше — «Теперь ты!». */
function KidScenario({ s, content, onDone }: { s: CompiledScenario; content: LoadedContent; onDone: () => void }) {
  const board = useRef<BoardHandle>(null);
  const pieces = useMemo(() => piecesFromScene(s.scenario.scene), [s.scenario.scene]);
  const steps = s.timelines.main?.steps ?? [];
  const [i, setI] = useState(0);
  const first = steps[0];
  const pose = useMemo(() => (first ? poseAt(first, 0) : { bodies: {}, ball: [50, 50] as [number, number], ballAttached: false }), [first]);
  useEffect(() => {
    let cancelled = false;
    audio.preload(s.scenario.steps.map((x) => phraseUrl(s, x.kid)));
    void (async () => {
      const title = s.scenario.kidTitle ?? s.scenario.title;
      await audio.play(phraseUrl(s, title), title);
      for (let k = 0; k < steps.length && !cancelled; k++) {
        setI(k);
        const text = s.scenario.steps[k]?.kid ?? "";
        // Сначала фраза, затем анимация шага — ребёнок слышит, что сейчас произойдёт.
        await audio.play(phraseUrl(s, text), text);
        if (cancelled) return;
        await runStep(board.current, steps[k]!);
        await new Promise((r) => setTimeout(r, 400));
      }
      if (cancelled) return;
      await audio.play(uiUrl(content, "your_turn"), uiText(content, "your_turn", "Теперь ты!"));
      if (!cancelled) onDone();
    })();
    return () => {
      cancelled = true;
      audio.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);
  return (
    <div className="kid-scenario">
      <div className="board-box board-box--kid">
        <Board ref={board} field={s.field} pieces={pieces} pose={pose} arrows={steps[i]?.segments} kid ariaLabel={s.scenario.kidTitle ?? s.scenario.title} />
      </div>
      <p className="kid-caption" aria-live="polite">
        {s.scenario.steps[i]?.kid}
      </p>
    </div>
  );
}

type Props = { content: LoadedContent; child: Child; limitMin: number; holdSec: number; onExit: () => void };

export function KidMode({ content, child, limitMin, holdSec, onExit }: Props) {
  const [plan, setPlan] = useState<Plan | null | undefined>(undefined);
  // План фиксируется один раз на сессию: ответы ребёнка меняют журнал, но не состав текущей сессии.
  const planned = useRef(false);
  useEffect(() => {
    if (planned.current) return;
    planned.current = true;
    void db.events.where("childId").equals(child.id).toArray().then((ev) => setPlan(makePlan(content, child, ev)));
  }, [content, child]);
  if (plan === undefined) return <div className="kid kid-scope" />;
  if (plan === null) return <div className="kid kid-scope"><p className="kid-caption">{t.common.loading}</p></div>;
  return <Session plan={plan} content={content} child={child} limitMin={limitMin} holdSec={holdSec} onExit={onExit} />;
}

function Session({ plan: initialPlan, content, child, limitMin, holdSec, onExit }: Props & { plan: Plan }) {
  const [state, dispatch] = useReducer(reduce, undefined, () => initSession(initialPlan, limitMin));
  const plan = state.plan;
  const [gate, setGate] = useState(false);
  const [portrait, setPortrait] = useState(false);
  const ended = useRef(false);

  // Таймер лимита — по реальному времени (сворачивание не останавливает отсчёт).
  useEffect(() => {
    const id = setInterval(() => dispatch({ type: "TICK", now: Date.now() }), 1000);
    return () => clearInterval(id);
  }, []);

  // Системная кнопка «назад» открывает родительский замок (UI-UX 5).
  useEffect(() => {
    history.pushState({ kid: true }, "");
    const onPop = () => {
      history.pushState({ kid: true }, "");
      setGate(true);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // На телефоне в книжной ориентации — подсказка «поверни телефон».
  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait) and (max-width: 599px)");
    const upd = () => setPortrait(mq.matches);
    upd();
    mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);
  useEffect(() => {
    if (portrait && audio.unlocked) void audio.play(uiUrl(content, "rotate"), uiText(content, "rotate", t.kid.rotate));
  }, [portrait, content]);

  useEffect(() => {
    if (state.phase === "reward") {
      audio.chime("reward");
      void audio.play(uiUrl(content, "reward"), uiText(content, "reward", "Ты молодец!")).then(() => setTimeout(() => dispatch({ type: "REWARD_DONE" }), 1500));
    }
    if (state.phase === "end" && !ended.current) {
      ended.current = true;
      void record(child.id, "kid_session_ended", { duration_sec: Math.round((Date.now() - (state.startedAt ?? Date.now())) / 1000), reason: state.endReason, stars: state.stars, tasks_done: state.taskIndex + (state.endReason === "done" ? 1 : 0) });
      void audio.play(uiUrl(content, "end"), uiText(content, "end", t.kid.end));
    }
  }, [state.phase, state.endReason, state.startedAt, state.stars, state.taskIndex, child.id, content]);

  const task = state.phase === "warmup" ? plan.warmup : state.phase === "task" ? plan.tasks[state.taskIndex] : null;
  const taskScenario = task ? findScenario(content, task.scenarioId)?.scenario : undefined;
  const scenario = findScenario(content, plan.scenarioId)?.scenario;
  const pathIndex = { start: -1, warmup: 0, scenario: 1, task: 2, reward: 3, end: 3 }[state.phase];

  return (
    <div className="kid kid-scope">
      <div className="kid__top">
        <ol className="kid__path" aria-label="Шаги сессии">
          {t.kid.sessionPath.map((label, i) => (
            <li key={label} className={i < pathIndex ? "done" : i === pathIndex ? "current" : ""} aria-label={label} />
          ))}
        </ol>
        <div className="kid__stars" aria-label={t.kid.stars(state.stars)}>
          {Array.from({ length: Math.max(3, state.stars) }, (_, i) => (
            <StarIcon key={i} size={36} filled={i < state.stars} />
          ))}
        </div>
        <button className="kid__lock" onClick={() => setGate(true)} aria-label={t.kid.lock}>
          🔒
        </button>
      </div>

      {state.phase === "start" ? (
        <div className="kid__center">
          <button
            className="kid__go"
            onClick={() => {
              void audio.unlock().then(() => {
                void audio.play(uiUrl(content, "start"), uiText(content, "start", t.kid.start));
                dispatch({ type: "START", now: Date.now() });
              });
              // Нативные сборки и установленное PWA — блокировка альбомной ориентации на телефоне.
              void (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.("landscape").catch(() => {});
            }}
          >
            {t.kid.start}
          </button>
        </div>
      ) : null}

      {state.phase === "scenario" && scenario ? <KidScenario s={scenario} content={content} onDone={() => dispatch({ type: "SCENARIO_DONE" })} /> : null}

      {(state.phase === "warmup" || state.phase === "task") && task && taskScenario ? (
        <QuizView
          key={`${state.phase}-${state.taskIndex}`}
          compiled={taskScenario}
          quizId={task.quizId}
          variant={task.variant}
          stage={child.stage}
          kid
          content={content}
          childId={child.id}
          onDone={(r) => (state.phase === "warmup" ? dispatch({ type: "WARMUP_DONE", star: r.firstGrade !== "bad" }) : dispatch({ type: "TASK_DONE", star: r.firstGrade !== "bad", now: Date.now() }))}
        />
      ) : null}

      {state.phase === "reward" ? (
        <div className="kid__center kid__reward">
          {Array.from({ length: Math.max(1, state.stars) }, (_, i) => (
            <span key={i} className="reward-star" style={{ animationDelay: `${i * 150}ms` }}>
              <StarIcon size={96} />
            </span>
          ))}
        </div>
      ) : null}

      {state.phase === "end" ? (
        <div className="kid__center kid__end">
          <img src="/icons/icon.svg" alt="" width={160} height={160} />
          <p className="kid-caption">{t.kid.end}</p>
          <p className="muted">{t.kid.callParent}</p>
        </div>
      ) : null}

      {portrait ? (
        <div className="kid__rotate" role="alert">
          <span aria-hidden="true">📱↻</span>
          <p>{t.kid.rotate}</p>
        </div>
      ) : null}

      {gate ? (
        <ParentGate
          holdSec={holdSec}
          onCancel={() => setGate(false)}
          onPass={() => {
            audio.stop();
            if (!ended.current) {
              ended.current = true;
              void record(child.id, "kid_session_ended", { duration_sec: Math.round((Date.now() - (state.startedAt ?? Date.now())) / 1000), reason: "parent_exit", stars: state.stars });
            }
            onExit();
          }}
        />
      ) : null}
    </div>
  );
}
